import { z } from 'zod';

import { getFeatureFlagsConfigAction } from '@kit/feature-flags/server';

import { FeatureFlagsSchema } from './feature-flags.config.schema';

export type { FeatureFlagsConfig } from '@kit/feature-flags';

type LanguagePriority = 'user' | 'application';
/**
 * Static feature flags config from environment variables
 * Used as fallback during build time or when database is unavailable
 * @deprecated Use getFeatureFlagsConfig() for database-backed flags
 */
const featuresFlagConfig = FeatureFlagsSchema.parse({
  enableThemeToggle: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_THEME_TOGGLE,
    true,
  ),
  enableAccountDeletion: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_PERSONAL_ACCOUNT_DELETION,
    true,
  ),
  enableTeamDeletion: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_DELETION,
    true,
  ),
  enableTeamAccounts: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS,
    false,
  ),
  enableTeamCreation: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_CREATION,
    false,
  ),
  enablePersonalAccountBilling: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_PERSONAL_ACCOUNT_BILLING,
    false,
  ),
  enableTeamAccountBilling: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_BILLING,
    false,
  ),
  languagePriority: process.env
    .NEXT_PUBLIC_LANGUAGE_PRIORITY as LanguagePriority,
  enableNotifications: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_NOTIFICATIONS,
    true,
  ),
  realtimeNotifications: getBoolean(
    process.env.NEXT_PUBLIC_REALTIME_NOTIFICATIONS,
    true,
  ),
  enableVersionUpdater: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_VERSION_UPDATER,
    true,
  ),
  enableSavedSignatures: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_SAVED_SIGNATURES,
    true,
  ),
  enableCoaching: getBoolean(process.env.NEXT_PUBLIC_ENABLE_COACHING, false),
  // Disabled by default (2026-07-05): the module is gated off until the
  // brand-ambassadors flow is ready. Flip NEXT_PUBLIC_ENABLE_AMBASSADORS=true
  // to re-enable.
  enableAmbassadors: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_AMBASSADORS,
    false,
  ),
  // No environment variable on purpose: the only switch is the
  // feature_flags row, which ships false (migration 20261108100000).
  enableAgencyContractSigning: false,
  enableMarketplaceCommerce: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_MARKETPLACE_COMMERCE,
    false,
  ),
} satisfies z.infer<typeof FeatureFlagsSchema>);

export default featuresFlagConfig;

/**
 * Fetch feature flags from database (async)
 * Use this in server components and server actions
 */
export async function getFeatureFlagsConfig() {
  return getFeatureFlagsConfigAction();
}

function getBoolean(value: unknown, defaultValue: boolean) {
  if (typeof value === 'string') {
    return value === 'true';
  }

  return defaultValue;
}
